/**
 * The single source of truth for every number in the platform.
 * Imported by the API (to populate dimension_stats/question_stats) and by tests.
 * Semantics pinned to Postgres/Excel: AVG, PERCENTILE_CONT(0.5), STDDEV_SAMP.
 * See contract/SPEC.md § Statistics and contract/fixtures/golden/stats-cases.json.
 */
import { MEAN_MIDPOINT, SD_MIDPOINT, HIGH_DIVERGENCE_SD, MIN_RELIABLE_N } from './analysis-config.ts';

export type Quadrant =
  | 'contested_gaps'
  | 'perception_gaps'
  | 'foundation_priorities'
  | 'consistent_strengths';

export interface StatSummary {
  /** Number of data points fed to the stats (NOT the displayed respondent n — see SPEC). */
  sampleSize: number;
  mean: number | null;
  median: number | null;
  sd: number | null;
  quadrant: Quadrant | null;
}

/** AVG; null when there are no values. */
export function mean(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  let sum = 0;
  for (const v of values) sum += v;
  return sum / values.length;
}

/** PERCENTILE_CONT(0.5): sort, linear-interpolate at rank 0.5*(n-1). null when empty. */
export function median(values: readonly number[]): number | null {
  const n = values.length;
  if (n === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const pos = 0.5 * (n - 1);
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  const frac = pos - lo;
  return s[lo]! + (s[hi]! - s[lo]!) * frac;
}

/** Sample standard deviation (n-1). null when fewer than 2 values — matches STDDEV_SAMP / Excel STDEV. */
export function sampleStdDev(values: readonly number[]): number | null {
  const n = values.length;
  if (n < 2) return null;
  const m = mean(values)!;
  let ss = 0;
  for (const v of values) ss += (v - m) ** 2;
  return Math.sqrt(ss / (n - 1));
}

/** Quadrant from a (mean, sd) pair. null if either is null. */
export function classifyQuadrant(m: number | null, sd: number | null): Quadrant | null {
  if (m === null || sd === null) return null;
  const highMean = m >= MEAN_MIDPOINT;
  const highSD = sd > SD_MIDPOINT;
  if (highMean) return highSD ? 'perception_gaps' : 'consistent_strengths';
  return highSD ? 'contested_gaps' : 'foundation_priorities';
}

/** Question-level "High Divergence" flag. */
export function isHighDivergence(sd: number | null): boolean {
  return sd !== null && sd > HIGH_DIVERGENCE_SD;
}

/**
 * "Interpret with caution" flag: the respondent count is too small to read the mean
 * confidently. Presentation only — does NOT change any computed number.
 */
export function isLowConfidence(n: number | null): boolean {
  return n !== null && n > 0 && n < MIN_RELIABLE_N;
}

/** Compute the full summary for a set of answer values. */
export function summarize(values: readonly number[]): StatSummary {
  const m = mean(values);
  const sd = sampleStdDev(values);
  return {
    sampleSize: values.length,
    mean: m,
    median: median(values),
    sd,
    quadrant: classifyQuadrant(m, sd),
  };
}
